import { TokenSet } from './Cognito'
import { Bookmark, User } from '../../functions/src/types';
import { captureException, captureMessage } from './Sentry';
import { debuglog } from './Log';

const AWS_ENDPOINT = 'https://livqm3xtbdffqxcptonzzn5szu0gjtmk.lambda-url.ap-southeast-2.on.aws';
const LOCAL_ENDPOINT = 'http://localhost:8080';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.host.startsWith('localhost')) {
    return LOCAL_ENDPOINT;
  }
  return AWS_ENDPOINT;
}

export interface ApiError {
  error: string;
  status?: number;
  statusText?: string;
  isNetworkError?: boolean;
}

export interface TokenSetWithPicture extends TokenSet {
  picture?: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    const messages = error
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry;
        }
        if (entry && typeof entry === 'object' && 'message' in entry && typeof entry.message === 'string') {
          return entry.message;
        }
        return null;
      })
      .filter((message): message is string => Boolean(message));
    if (messages.length > 0) {
      return messages.join(', ');
    }
  }
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return fallback;
};

const parseApiErrorResponse = async (response: Response, fallback: string): Promise<ApiError> => {
  let errorPayload: unknown;
  try {
    errorPayload = await response.json();
  }
  catch {
    try {
      const errorText = await response.text();
      errorPayload = errorText || undefined;
    }
    catch {
      errorPayload = undefined;
    }
  }

  const payloadError =
    errorPayload && typeof errorPayload === 'object' && 'error' in errorPayload
      ? errorPayload.error
      : errorPayload;

  return {
    error: getErrorMessage(payloadError, fallback),
    status: response.status,
    statusText: response.statusText || undefined,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleApiError = (error?: any, fallback = 'Unknown error'): ApiError => {
  console.error('API error', error);
  return {
    error: error?.message ? error.message : fallback,
    isNetworkError: error instanceof TypeError,
  };
};

export const formatApiError = (error?: ApiError, fallback = 'Unknown error'): string => {
  if (!error) {
    return fallback;
  }
  const detail = error.error || fallback;
  if (error.status) {
    const statusText = error.statusText ? ` ${error.statusText}` : '';
    return `HTTP ${error.status}${statusText}: ${detail}`;
  }
  if (error.isNetworkError) {
    return `Network error: ${detail}`;
  }
  return detail;
};

export interface StashResult {
  bookmark: Bookmark;
  alreadySaved: boolean;
}

export interface StashResponse {
  result?: StashResult;
  error?: ApiError;
}

const getStashSentryExtras = (user: User, url: string, error?: ApiError) => ({
  operation: 'stash',
  userId: user.uid,
  url,
  httpStatus: error?.status,
  httpStatusText: error?.statusText,
  isNetworkError: error?.isNetworkError ?? false,
  errorMessage: error?.error,
  apiBaseUrl: getApiBaseUrl(),
});

/**
 * Exchange an OAuth code for a token set
 */
export const exchangeToken = async (code: string) => {
  // make a GET request to the server to exchange the code for a token
  try {
    const response = await fetch(`${getApiBaseUrl()}/token?code=${code}`);
    if (response.status === 200) {
      const data = await response.json();
      if (data && !data.error) {
        return data as TokenSetWithPicture;
      }
      else {
        console.error('failed to exchange token', data.error);
      }
    }
  }
  catch (error) {
    console.error('failed to exchange token', error);
  }
};

/**
 * Use refresh token to get a new token set
 */
export const refreshToken = async (refreshToken: string) => {
  // make a POST request to the server to refresh the token
  try {
    const response = await fetch(`${getApiBaseUrl()}/refresh`, {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    if (data && !data.error) {
      return {
        ...data,
        refresh_token: refreshToken
      } as TokenSet;

    }
    else {
      console.error('failed to refresh token', data.error);
    }
  }
  catch (error) {
    console.error('failed to refresh token', error);
  }
}

/**
 * Stash a bookmark
 * @returns the bookmark if successful, or true if successful but no bookmark was returned (already saved)
 */
export const stash = async (user: User, url: string): Promise<StashResponse> => {
  // make a POST request to the server to stash the bookmark
  try {
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/stash`, {
      method: 'POST',
      body: JSON.stringify({ url }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      }
    });
    if (!response.ok) {
      const apiError = await parseApiErrorResponse(response, 'Failed to stash bookmark');
      console.error('failed to stash bookmark', apiError);
      captureMessage(
        `failed to stash bookmark: ${formatApiError(apiError)}`,
        getStashSentryExtras(user, url, apiError)
      );
      return { error: apiError };
    }
    const data = await response.json();
    if (!data.error) {
      return {
        result: {
          bookmark: {
            ...data.bookmark,
            addedOn: new Date(data.bookmark.addedOn)
          } as Bookmark,
          alreadySaved: data.alreadySaved
        }
      };
    }
    else {
      const apiError = { error: getErrorMessage(data.error, 'Failed to stash bookmark') };
      console.error('failed to stash bookmark', apiError);
      captureMessage(
        `failed to stash bookmark: ${formatApiError(apiError)}`,
        getStashSentryExtras(user, url, apiError)
      );
      return { error: apiError };
    }
  }
  catch (error) {
    console.error('failed to save bookmark', error);
    const apiError = handleApiError(error, 'Failed to save bookmark');
    captureException(error, getStashSentryExtras(user, url, apiError));
    return { error: apiError };
  }
};

/**
 * Update a bookmark
 * @returns the bookmark if successful, or true if successful but no bookmark was returned (already saved)
 */
export const updateExistingBookmark = async (user: User, bookmark: Bookmark) => {
  // make a POST request to the server to update the bookmark
  try {
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/bookmarks`, {
      method: 'PUT',
      body: JSON.stringify(bookmark),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      }
    });
    const data = await response.json();
    if (!data.error) {
      debuglog('bookmark updated', data);
      return {
        ...data,
        addedOn: new Date(data.addedOn)
      } as Bookmark;
    }
    else {
      console.error('failed to update bookmark', data.error);
      captureMessage(`failed to update bookmark: ${data.error}`);
    }
  }
  catch (error) {
    console.error('failed to update bookmark', error);
    captureException(error);
  }
};

/**
 * Paginated response from the bookmarks API
 */
export interface BookmarksPage {
  bookmarks: Bookmark[];
  nextToken?: string;
}

export interface BulkTagUpdatePayload {
  bookmarkIds: string[];
  addTags?: string[];
  removeTags?: string[];
}

export interface BulkTagUpdateResponse {
  result?: Bookmark[];
  error?: ApiError | string;
}

/**
 * Get bookmarks for a user with pagination support
 * @param user the user
 * @param searchText optional search text
 * @param nextToken optional pagination token for next page
 * @returns paginated bookmarks response
 */
export const getAllTags = async (user: User): Promise<string[]> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/tags`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      }
    });
    if (response.ok) {
      const data = await response.json();
      return data.tags || [];
    }
    return [];
  } catch (error) {
    console.error('failed to get tags', error);
    return [];
  }
};

export const getAllBookmarks = async (
  user: User,
  searchText?: string,
  nextToken?: string,
  tags?: string[]
): Promise<{ result?: BookmarksPage, error?: ApiError }> => {
  // make a GET request to the server to get bookmarks
  try {
    const fetchOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      }
    };
    // build a query string for searchText, tags, and nextToken:
    let fetchParams = '';
    if (searchText) {
      fetchParams += `?search=${encodeURIComponent(searchText)}`;
    }
    if (tags && tags.length > 0) {
      fetchParams += fetchParams.length > 0 ? '&' : '?';
      fetchParams += `tags=${encodeURIComponent(tags.join(','))}`;
    }
    if (nextToken) {
      fetchParams += fetchParams.length > 0 ? '&' : '?';
      fetchParams += `nextToken=${encodeURIComponent(nextToken)}`;
    }

    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/bookmarks${fetchParams}`, fetchOptions);
    if (!response.ok) {
      return { error: await parseApiErrorResponse(response, 'Failed to fetch bookmarks') };
    }
    const data = await response.json();
    if (!data.error) {
      const mappedBookmarks = data.bookmarks.map((bookmark: Bookmark) => {
        return {
          ...bookmark,
          addedOn: new Date(bookmark.addedOn)
        } as Bookmark
      });
      return {
        result: {
          bookmarks: mappedBookmarks,
          nextToken: data.nextToken
        }
      };
    }
    else {
      return { error: data.error };
    }
  }
  catch (error) {
    console.error('failed to get bookmarks', error);
    return { error: handleApiError(error, 'Failed to fetch bookmarks') };
  }
};

export const uploadPocketHTMLfileForImport = async (user: User, file: string):
  Promise<{ result?: { successCount: number, failureCount: number, alreadyExistsCount: number }, error?: ApiError }> => {
  try {
    const fetchOptions = {
      method: 'POST',
      body: file,
      headers: {
        'Authorization': `Bearer ${user.access_token}`,
        'Content-Type': 'text/html'
      }
    };
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/pocket`, fetchOptions);
    if (!response.ok) {
      return { error: await parseApiErrorResponse(response, 'Failed to import bookmarks') };
    }
    const data = await response.json();
    if (!data.error) {
      return { result: data };
    }
    else {
      return { error: data.error };
    }
  }
  catch (error) {
    console.error('failed to upload', error);
    return { error: handleApiError(error, 'Failed to import bookmarks') };
  }
};

export const bulkUpdateBookmarkTags = async (user: User, payload: BulkTagUpdatePayload): Promise<BulkTagUpdateResponse> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/bookmarks/bulk-tags`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      }
    });
    const data = await response.json();
    if (!response.ok) {
      return { error: data?.error || 'Failed to update tags' };
    }
    if (!data.error) {
      return {
        result: (data.bookmarks || []).map((bookmark: Bookmark) => ({
          ...bookmark,
          addedOn: new Date(bookmark.addedOn)
        } as Bookmark))
      };
    }
    return { error: data.error };
  }
  catch (error) {
    console.error('failed to bulk update bookmark tags', error);
    captureException(error);
    return { error: handleApiError(error, 'Failed to update tags') };
  }
};

/**
 * Delete a bookmark by URL
 */
export const deleteBookmark = async (user: User, id: string) => {
  // make a DELETE request to the server to delete the bookmark
  try {
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/bookmarks`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      },
      body: JSON.stringify({ id })
    });
    if (response.status === 200) {
      return true;
    }
    return false;
  }
  catch (error) {
    console.error('failed to delete bookmark', error);
    return false;
  }
};

/**
 * Export bookmarks for a user
 */
export const exportBookmarks = async (user: User, format: string) => {
  // make a GET request to the server to export all bookmarks
  try {
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/export/${format}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      }
    });
    if (response.status === 200) {
      const data = await response.text();
      return data;
    }
  }
  catch (error) {
    console.error('failed to export bookmarks', error);
  }
};

export interface BackupApiKey {
  apiKey: string;
  expiresAt: string;
  url: string;
}

/**
 * Generate a new backup API key for the user, used to authenticate
 * external requests to the JSON backup export endpoint
 */
export const generateBackupApiKey = async (user: User, expiresInDays: number): Promise<BackupApiKey | ApiError> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/${user.uid}/backup/api-key`, {
      method: 'POST',
      body: JSON.stringify({ expiresInDays }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      }
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      return { error: data?.error || 'Failed to generate backup API key' };
    }
    return {
      apiKey: data.apiKey,
      expiresAt: data.expiresAt,
      url: `${getApiBaseUrl()}/external/${user.uid}/backup`,
    };
  }
  catch (error) {
    console.error('failed to generate backup API key', error);
    return handleApiError(error, 'Failed to generate backup API key');
  }
};
